import React from 'react';
import mapKeys from 'lodash/mapKeys';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import {
  detectUnlockedStatus,
  renderAssessmentProgress as renderAssessmentProgressUtil,
} from 'legacy-packages/unit-outline/src/components/_shared/util';
import { SubunitAssessmentTypes } from 'legacy-packages/unit-outline/src/constants';
import LockedAssignment from '../components/LockedAssignment';
import sendTelemetryEvent from '../components/sendTelemetryEvent';

export const formatUpdatedAssignment = (
  classes = [],
  updateAssignments = [],
  assessmentData = {},
) => {
  const { questions = [] } = assessmentData;
  return updateAssignments.map((a) => {
    let assignment = mapKeys(a, (value, key) => camelCase(key));
    let { progress, item, sectionId, isIndividualAssignment, students } =
      assignment;
    const assignmentStudentIds = students.map((s) =>
      Number.parseInt(s.studentId, 10),
    );
    // find the list of students in the classes
    let section =
      classes.find((c) => c.id.toString() === sectionId.toString()) || {};
    const camelProgress = mapKeys(progress, (value, key) => camelCase(key));
    const { name: sectionName, students: sectionStudents } = section;
    const camelStudents = sectionStudents
      .map((s) => mapKeys(s, (value, key) => camelCase(key)))
      .map((s) => {
        return { ...s, userId: s.id.toString() };
      });
    const assignmentStudents = isIndividualAssignment
      ? camelStudents.filter((s) =>
          assignmentStudentIds.includes(Number.parseInt(s.id, 10)),
        )
      : camelStudents;

    return {
      ...assignment,
      // default newly assignments has not been started
      progress: { ...camelProgress, notStarted: assignmentStudents.length },
      students: assignmentStudents,
      sectionStudents: camelStudents,
      assessment: {
        id: item.id,
        itemId: item.id.toString(),
        isPracticeExam: item.is_practice_exam,
        isCbPracticeQuiz: item.is_cb_practice_quiz,
        isSPP: item.is_spp,
        questions,
      },
      sectionName,
      __typename: 'Assignment',
    };
  });
};

const useAssessmentProgressComponent = (props) => {
  const {
    router,
    subjectData,
    subjectId,
    subunitType,
    disablePpcAutoAssignFlag,
    resourceStatuses,
    unitId,
    unitNumber,
    unitTitle,
    isAdminOrCoordinator,
    isTeacher,
    isStudent,
    assessmentDataCache,
    assignments: assignmentsRaw,
  } = props;
  let assignments = [...assignmentsRaw];

  // render the progress
  const renderAssessmentProgress = (
    isProgressAvailable,
    assignmentsForResource,
    displayName,
    resource,
    scope,
    assessmentId,
  ) => {
    const isPPC = subunitType === SubunitAssessmentTypes.PPC;
    return renderAssessmentProgressUtil(
      isProgressAvailable,
      assignmentsForResource,
      displayName,
      resource,
      isAdminOrCoordinator,
      subjectId,
      isPPC,
      false,
      assignments,
      scope,
      subunitType,
      disablePpcAutoAssignFlag,
      unitNumber,
      unitTitle,
      assessmentId,
    );
  };

  const getProgressCheckResourceAction = (resource) => {
    const { assessmentId, displayName } = resource;
    const isMCQ = displayName.toLowerCase().includes('mcq');

    const assessmentResourceStatusKey = Object.keys(resourceStatuses).find(
      (resourceStatusKey) =>
        resourceStatuses[resourceStatusKey].assessmentId === assessmentId,
    );
    const connectLeanResourceScope = [
      'units',
      `unit-${unitId}`,
      'resources',
      assessmentResourceStatusKey,
    ];

    const {
      isAssigned,
      assignmentOverview,
      assignResponses = [],
    } = resourceStatuses[assessmentResourceStatusKey] || {};
    const updatedAssignments = get(assignmentOverview, 'assignments', []);

    let assignmentsForResource = [];

    const isAssetNotAutoAssigned =
      subunitType === SubunitAssessmentTypes.PE ||
      (subunitType === SubunitAssessmentTypes.PPC && disablePpcAutoAssignFlag);
    if (!isStudent) {
      /**
       * due to PE not been pre-assigned we don;t have them
       * in the list of assignment when it's loaded
       * as such anytime we assign a PE assessment
       * will re-inject the assignResponses into the list
       * of loadedAssignment, this help for proper assignment checking.
       */
      if (assignResponses.length > 0 && isAssetNotAutoAssigned) {
        let recentUpdatedAssignment = [];
        recentUpdatedAssignment = formatUpdatedAssignment(
          subjectData.classes,
          assignResponses,
          // assessment dataCache to get question information for this assessment
          assessmentDataCache[resource.assessmentId],
        );
        assignments = assignments.concat(recentUpdatedAssignment);
      }

      assignmentsForResource = (assignments || [])
        .filter(
          (assignment) =>
            assignment.assessment.itemId === resource.assessmentId,
        )
        .map((assignment) => {
          const updatedAssignment = updatedAssignments.find(
            (a) => a.id && a.id.toString() === assignment.id,
          );

          let result = assignment;
          if (updatedAssignment) {
            const {
              opened,
              starts_at: startsAt,
              status,
              teacher_assigned: teacherAssigned,
            } = updatedAssignment;
            result = {
              ...result,
              opened,
              startsAt,
              status,
              teacherAssigned,
            };
          }

          return result;
        });
      assignmentsForResource = assignmentsForResource.sort(
        (assignmentA, assignmentB) =>
          assignmentA.sectionName > assignmentB.sectionName ? 1 : -1,
      );
    }

    const isProgressAvailable = isAdminOrCoordinator ? !isMCQ : isAssigned;

    const assessmentProgressComponent = renderAssessmentProgress(
      isProgressAvailable,
      assignmentsForResource,
      displayName,
      resource,
      connectLeanResourceScope,
      assessmentId,
    );

    if (assessmentProgressComponent) {
      return assessmentProgressComponent;
    }

    const isUnlocked = detectUnlockedStatus(
      assignmentsForResource,
      assessmentId,
    );

    if (!isUnlocked) {
      const assignProps = {
        resource,
        unitNumber,
        assessmentId,
        unitTitle,
        isTeacher,
        router,
        subjectId,
        scope: connectLeanResourceScope,
        isAdminOrCoordinator,
        subunitType,
        disablePpcAutoAssignFlag,
        onAssignCallback: (totalAssignees, assessmentTitle) => {
          sendTelemetryEvent(
            subjectId,
            totalAssignees.length,
            assessmentId,
            assessmentTitle,
          );
        },
      };
      return <LockedAssignment {...assignProps} />;
    }

    return assessmentProgressComponent;
  };

  return {
    getProgressCheckResourceAction,
  };
};

export default useAssessmentProgressComponent;
